/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import merge from 'lodash.merge';
import { AzureCsAccountKind } from 'src/core/stores/AzureCsAccountStore';
import { ServiceKey } from 'src/core/stores/AzureSessionStore';
import { HandlerContext } from 'src/core/types/handler';
import { ApiName, ApiVersion, getEnvironment } from 'src/core/utils/api/environment';
import { GlobalEnvironmentMetadata } from 'src/core/utils/GlobalEnvironmentMetadata';

export type GetEnv = (obj?: {
    apiName?: ApiName;
    apiVersion?: ApiVersion;
    currentResourceKind?: AzureCsAccountKind;
}) => ReturnType<typeof getEnvironment>;

export type ServiceContext<T = HandlerContext> = T & {
    context: { serviceKey: ServiceKey; currentResourceRegion: string };
    getEnvironment: GetEnv;
};

/**
 * A factory function used for initializing services.
 * It reads values from the GlobalEnvironmentMetadata
 * singleton and injects these values into the given service.
 */
export const createService = <Params = HandlerContext, Return = object>(service: (obj: ServiceContext<Params>) => Return) => (
    obj?: Params
) => {
    const { serviceKey, endpoint, currentResourceKind: defaultCurrentResourceKind, region } = GlobalEnvironmentMetadata.instance;

    const internalGetEnvironment: GetEnv = ({ apiName, apiVersion, currentResourceKind } = {}) =>
        getEnvironment({
            apiName,
            apiVersion,
            currentResourceEndpoint: endpoint,
            currentResourceKind: currentResourceKind ?? defaultCurrentResourceKind
        });

    return service(merge(obj, { context: { serviceKey, currentResourceRegion: region }, getEnvironment: internalGetEnvironment }));
};
